/**
 * Stroke geometry → SVG path data.
 *
 * Pure and framework-free so it can be unit tested without a DOM. The output is
 * what gets persisted, so the grammar here has to stay inside the subset the
 * API's `validateSignaturePaths` accepts: `M`, `L`, `Q`, and numbers. No arcs,
 * no cubics, no shorthand commands.
 */

export interface Point {
  x: number
  y: number
}

/** Coordinates are rounded to this many decimals — sub-pixel precision nobody can see. */
const PRECISION = 2

const round = (n: number): number => Number(n.toFixed(PRECISION))

/**
 * Convert a captured stroke to a smoothed SVG path.
 *
 * Smoothing uses quadratic segments through the midpoints between consecutive
 * samples, with each raw sample as the control point. That is the standard
 * trick for turning jittery pointer samples into something that reads as
 * handwriting, and it needs no lookahead — the path can be extended as points
 * arrive rather than recomputed from scratch.
 *
 * Degenerate strokes still produce something visible: a single tap becomes a
 * dot (a zero-length line, which renders as a round cap), because a signature
 * with a deliberate dot in it should not silently lose the dot.
 */
export function strokeToPath(points: Point[]): string {
  if (points.length === 0) return ''

  const [first] = points
  if (points.length === 1) {
    return `M${round(first.x)} ${round(first.y)} L${round(first.x)} ${round(first.y)}`
  }

  if (points.length === 2) {
    return `M${round(first.x)} ${round(first.y)} L${round(points[1].x)} ${round(points[1].y)}`
  }

  let d = `M${round(first.x)} ${round(first.y)}`

  for (let i = 1; i < points.length - 1; i += 1) {
    const current = points[i]
    const next = points[i + 1]
    const midX = round((current.x + next.x) / 2)
    const midY = round((current.y + next.y) / 2)
    d += ` Q${round(current.x)} ${round(current.y)} ${midX} ${midY}`
  }

  const last = points[points.length - 1]
  d += ` L${round(last.x)} ${round(last.y)}`

  return d
}

/**
 * Drop samples that land essentially on top of the previous one.
 *
 * A stationary pointer still fires `pointermove`, and without this a signature
 * held still for a second carries dozens of redundant points into the stored
 * payload for no visual gain.
 */
export function isSignificantMove(previous: Point, next: Point, minDistance = 1.5): boolean {
  const dx = next.x - previous.x
  const dy = next.y - previous.y
  return dx * dx + dy * dy >= minDistance * minDistance
}
